import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useProfile } from './useProfile';

export interface NutritionLog {
  id: string;
  log_date: string;
  protein_grams: number;
  calories: number | null;
  water_liters: number | null;
  notes: string | null;
}

export const useNutritionTracking = () => {
  const { user } = useAuth();
  const { profile } = useProfile();
  const [nutritionLogs, setNutritionLogs] = useState<NutritionLog[]>([]);
  const [nutritionGoals, setNutritionGoals] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [todaysProtein, setTodaysProtein] = useState<number>(0);

  const fetchTodaysProtein = async () => {
    if (!user) return;
    
    const today = new Date().toISOString().split('T')[0];
    
    // Calculate from meals/food_entries (new comprehensive system)
    try {
      const { data: todayMeals, error: mealsError } = await supabase
        .from('meals')
        .select('id')
        .eq('user_id', user.id)
        .eq('meal_date', today);
      
      if (mealsError) throw mealsError;
      
      if (todayMeals && todayMeals.length > 0) {
        const mealIds = todayMeals.map(m => m.id);
        
        const { data: foodEntries, error: entriesError } = await supabase
          .from('food_entries')
          .select('protein')
          .in('meal_id', mealIds);
        
        if (entriesError) throw entriesError;
        
        if (foodEntries && foodEntries.length > 0) {
          const totalProtein = foodEntries.reduce((sum, entry) => {
            return sum + (entry.protein || 0);
          }, 0);
          
          setTodaysProtein(Math.round(totalProtein));
          return;
        }
      }
    } catch (error) {
      console.error('Error calculating today protein from meals:', error);
    }
    
    setTodaysProtein(0);
  };

  const fetchNutritionGoals = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('user_nutrition_goals')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .maybeSingle();

      if (error) throw error;
      setNutritionGoals(data);
    } catch (error) {
      console.error('Error fetching nutrition goals:', error);
    }
  };

  const fetchNutritionLogs = async (days = 30) => {
    if (!user) return;
    
    setLoading(true);
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      
      const { data, error } = await supabase
        .from('nutrition_logs')
        .select('*')
        .eq('user_id', user.id)
        .gte('log_date', startDate.toISOString().split('T')[0])
        .order('log_date', { ascending: false });

      if (error) throw error;
      setNutritionLogs(data || []);
      
      // Fetch today's protein after loading nutrition logs
      await fetchTodaysProtein();
    } catch (error) {
      console.error('Error fetching nutrition logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const logNutrition = async (nutritionData: Omit<NutritionLog, 'id'>) => {
    if (!user) return;

    try {
      console.log('Logging nutrition data:', nutritionData);
      
      const { data, error } = await supabase
        .from('nutrition_logs')
        .upsert([{
          ...nutritionData,
          user_id: user.id
        }], {
          onConflict: 'user_id,log_date',
          ignoreDuplicates: false
        })
        .select()
        .single();

      if (error) {
        console.error('Error logging nutrition:', error);
        throw error;
      }
      
      console.log('Nutrition logged successfully:', data);
      await fetchNutritionLogs();
      return data;
    } catch (error) {
      console.error('Error logging nutrition:', error);
      throw error;
    }
  };

  const getProteinGoal = () => {
    // Get from user's nutrition goals if available
    if (nutritionGoals?.daily_protein) {
      return nutritionGoals.daily_protein;
    }
    
    // Calculate based on body weight if profile available
    if (profile?.weight_kg) {
      return Math.round(profile.weight_kg * 2); // 2g per kg bodyweight
    }
    
    // Fallback default
    return 120;
  };

  const getTodaysProtein = () => {
    return todaysProtein;
  };

  const getWeeklyProteinAverage = () => {
    if (nutritionLogs.length === 0) return 0;
    
    const last7Days = nutritionLogs.slice(0, 7);
    const total = last7Days.reduce((sum, log) => sum + log.protein_grams, 0);
    return Math.round(total / last7Days.length);
  };

  const getProteinTrend = () => {
    if (nutritionLogs.length < 14) return 'stable';
    
    const recent7Days = nutritionLogs.slice(0, 7);
    const previous7Days = nutritionLogs.slice(7, 14);
    
    const recentAvg = recent7Days.reduce((sum, log) => sum + log.protein_grams, 0) / recent7Days.length;
    const previousAvg = previous7Days.reduce((sum, log) => sum + log.protein_grams, 0) / previous7Days.length;
    
    if (recentAvg > previousAvg + 10) return 'improving';
    if (recentAvg < previousAvg - 10) return 'declining';
    return 'stable';
  };

  const getProteinStreaks = () => {
    let currentStreak = 0;
    const goal = getProteinGoal();
    
    for (const log of nutritionLogs) {
      if (log.protein_grams >= goal) {
        currentStreak++;
      } else {
        break;
      }
    }
    
    return currentStreak;
  };

  useEffect(() => {
    if (user) {
      fetchNutritionGoals();
      fetchNutritionLogs();
    }
  }, [user]);
  
  // Refetch today's protein when component mounts or when user changes
  useEffect(() => {
    if (user && nutritionLogs.length >= 0) {
      fetchTodaysProtein();
    }
  }, [user, nutritionLogs]);

  return {
    nutritionLogs,
    loading,
    logNutrition,
    getProteinGoal,
    getTodaysProtein,
    getWeeklyProteinAverage,
    getProteinTrend,
    getProteinStreaks,
    refetch: fetchNutritionLogs,
    refreshTodaysProtein: fetchTodaysProtein
  };
};